import React, { useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getTmsApiUrl } from '../utils/apiUrls';

const BACKEND_URL = getTmsApiUrl();

const STATUS_COLORS = {
  uploaded: '#10b981',
  needed:   '#6b7280',
  waived:   '#f59e0b',
};

const UNMAPPED_DROP_ID = '__unmapped__';

function DeveloperSkySlopeChecklists() {
  const { user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  const [userEmail, setUserEmail]       = useState('');
  const [userId, setUserId]             = useState('');
  const [checklists, setChecklists]     = useState(null);
  const [userInfo, setUserInfo]         = useState(null);
  const [officeGuid, setOfficeGuid]     = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  // Drag state: { doc_type, groupIndex } — null when not dragging
  const [dragState, setDragState]       = useState(null);
  // dropTarget: { groupIndex, itemId } — the row currently being hovered
  const [dropTarget, setDropTarget]     = useState(null);
  // Per-group action feedback: { [groupIndex]: 'saving' | 'refreshing' | 'done' | 'error:msg' }
  const [groupStatus, setGroupStatus]   = useState({});

  const lastUserEmail = useRef(userEmail);

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    return ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'].includes(email);
  }, [user]);

  if (!isAuthenticated || !isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  const fetchChecklists = async (targetEmail, targetId) => {
    const email = targetEmail ?? userEmail;
    const id    = targetId ?? userId;
    if (!email && !id) return;
    try {
      setLoading(true);
      setError('');
      setChecklists(null);
      setUserInfo(null);
      setOfficeGuid(null);
      setGroupStatus({});

      const queryParams = { requester_email: user?.email || '' };
      if (email) queryParams.user_email = email;
      else if (id) queryParams.user_id = id;

      const response = await axios.get(`${BACKEND_URL}/checklists`, { params: queryParams });
      setChecklists(response.data.checklists || []);
      setUserInfo(response.data.user);
      setOfficeGuid(response.data.office_guid || null);

      const expanded = {};
      (response.data.checklists || []).forEach((g, i) => { expanded[i] = true; });
      setExpandedGroups(expanded);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to fetch checklists');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') fetchChecklists(); };
  const toggleGroup   = (i) => setExpandedGroups(prev => ({ ...prev, [i]: !prev[i] }));

  // ── Drag handlers ──────────────────────────────────────────────────────────

  const handleDragStart = (e, doc_type, groupIndex) => {
    setDragState({ doc_type, groupIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDragState(null);
    setDropTarget(null);
  };

  const handleDragOver = (e, groupIndex, itemId, typeId) => {
    // Only accept drops within the same group and only if that group has a type_id
    if (!dragState || dragState.groupIndex !== groupIndex || !typeId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget({ groupIndex, itemId });
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the row entirely (not a child element)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTarget(null);
    }
  };

  const handleDrop = async (e, group, itemId, itemNameLower) => {
    e.preventDefault();
    setDropTarget(null);
    if (!dragState) return;

    const { doc_type, groupIndex } = dragState;
    setDragState(null);

    const typeId = group.checklist_type_id;
    const guid   = group.office_guid;
    if (!typeId || !guid) return;

    const gKey = `${groupIndex}`;
    setGroupStatus(prev => ({ ...prev, [gKey]: 'saving' }));

    try {
      await axios.post(`${BACKEND_URL}/checklist/mapping/item`, {
        requester_email:  user?.email || '',
        office_guid:      guid,
        checklist_type_id: typeId,
        doc_type,
        item_name:        itemNameLower,
      });

      // Update local state immediately so the UI reflects the change
      setChecklists(prev => prev.map((g, i) => {
        if (i !== groupIndex) return g;
        const unmapped = (g.unmapped_doc_types || []).filter(d => d !== doc_type);
        return {
          ...g,
          unmapped_doc_types: unmapped,
          items: g.items.map(it => {
            // Remove doc_type from wherever it currently lives
            const filtered = (it.mapped_doc_types || []).filter(d => d !== doc_type);
            // Add to the target item
            if (it.id === itemId) {
              return { ...it, mapped_doc_types: [...filtered, doc_type] };
            }
            return { ...it, mapped_doc_types: filtered };
          }),
        };
      }));

      setGroupStatus(prev => ({ ...prev, [gKey]: 'done' }));
      setTimeout(() => setGroupStatus(prev => {
        const next = { ...prev };
        delete next[gKey];
        return next;
      }), 2000);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Save failed';
      setGroupStatus(prev => ({ ...prev, [gKey]: `error:${msg}` }));
    }
  };

  const handleDropToUnmapped = async (e, group, groupIndex) => {
    e.preventDefault();
    setDropTarget(null);
    if (!dragState) return;

    const { doc_type } = dragState;
    setDragState(null);

    const typeId = group.checklist_type_id;
    const guid   = group.office_guid;
    if (!typeId || !guid) return;

    const gKey = `${groupIndex}`;
    setGroupStatus(prev => ({ ...prev, [gKey]: 'saving' }));

    try {
      await axios.post(`${BACKEND_URL}/checklist/mapping/item`, {
        requester_email:   user?.email || '',
        office_guid:       guid,
        checklist_type_id: typeId,
        doc_type,
        item_name:         null,
      });

      setChecklists(prev => prev.map((g, i) => {
        if (i !== groupIndex) return g;
        const unmapped = [...(g.unmapped_doc_types || [])];
        if (!unmapped.includes(doc_type)) unmapped.push(doc_type);
        unmapped.sort();
        return {
          ...g,
          unmapped_doc_types: unmapped,
          items: g.items.map(it => ({
            ...it,
            mapped_doc_types: (it.mapped_doc_types || []).filter(d => d !== doc_type),
          })),
        };
      }));

      setGroupStatus(prev => ({ ...prev, [gKey]: 'done' }));
      setTimeout(() => setGroupStatus(prev => {
        const next = { ...prev };
        delete next[gKey];
        return next;
      }), 2000);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Save failed';
      setGroupStatus(prev => ({ ...prev, [gKey]: `error:${msg}` }));
    }
  };

  // ── Refresh Firestore mapping cache for a group ────────────────────────────

  const handleRefreshMapping = async (group, groupIndex) => {
    const typeId = group.checklist_type_id;
    const guid   = group.office_guid;
    if (!typeId || !guid) return;

    const gKey = `${groupIndex}`;
    setGroupStatus(prev => ({ ...prev, [gKey]: 'refreshing' }));

    try {
      await axios.post(`${BACKEND_URL}/checklist/mapping/refresh`, {
        requester_email:  user?.email || '',
        office_guid:      guid,
        checklist_type_id: typeId,
      });
      setGroupStatus(prev => ({ ...prev, [gKey]: 'done' }));
      // Re-fetch so firestore_mapping_loaded reflects the cleared state
      await fetchChecklists(userEmail, userId);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Refresh failed';
      setGroupStatus(prev => ({ ...prev, [gKey]: `error:${msg}` }));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • SkySlope Checklists</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <DeveloperNavigation />
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>User Email</label>
          <input
            value={userEmail}
            onChange={e => { setUserEmail(e.target.value); lastUserEmail.current = e.target.value; if (e.target.value) setUserId(''); }}
            onKeyDown={handleKeyDown}
            placeholder="e.g. abbysellsoklahoma@gmail.com"
            style={{ width: '280px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>User ID</label>
          <input
            value={userId}
            onChange={e => { setUserId(e.target.value); if (e.target.value) setUserEmail(''); }}
            onKeyDown={handleKeyDown}
            placeholder="e.g. 20"
            style={{ width: '100px' }}
          />
        </div>
        <button onClick={() => fetchChecklists()} disabled={loading || (!userEmail && !userId)}>
          Load Checklists
        </button>
        <button onClick={() => { setUserEmail(''); setUserId(''); setChecklists(null); setUserInfo(null); setError(''); setOfficeGuid(null); }}>
          Clear
        </button>
      </div>

      {loading && <LoadingSpinner />}
      {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

      {/* User info */}
      {userInfo && (
        <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: '#374151' }}>
          Showing checklists for <strong>{userInfo.email}</strong> (id={userInfo.id})
          {' · '}{checklists?.length ?? 0} checklist group(s)
          {officeGuid && <span style={{ color: '#9ca3af' }}> · office={officeGuid}</span>}
        </div>
      )}

      {checklists && (
        <div style={{ marginBottom: '12px', fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic' }}>
          Drag a doc-type badge onto a checklist item row to assign it, or onto the unmapped bucket to remove its mapping.
          Changes apply office-wide for that checklist type. Only groups with a type_id support editing.
        </div>
      )}

      {checklists && checklists.length === 0 && (
        <div style={{ color: '#6b7280' }}>No checklist items found for this user.</div>
      )}

      {checklists && checklists.map((group, groupIndex) => {
        const isExpanded   = expandedGroups[groupIndex] !== false;
        const uploadedCount = group.items.filter(it => it.status === 'uploaded').length;
        const pct          = group.items.length > 0 ? Math.round(uploadedCount / group.items.length * 100) : 0;
        const canEdit      = !!group.checklist_type_id;
        const gKey         = `${groupIndex}`;
        const gStatus      = groupStatus[gKey] || '';
        const isDraggingInGroup = dragState?.groupIndex === groupIndex;

        return (
          <div key={groupIndex} style={{ marginBottom: '16px', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
            {/* Group header */}
            <div
              onClick={() => toggleGroup(groupIndex)}
              style={{
                display: 'flex', alignItems: 'center', gap: '16px',
                padding: '10px 16px', background: '#f8fafc', cursor: 'pointer',
                borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
                userSelect: 'none',
              }}
            >
              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                {group.scope === 'listing' ? '📋 Listing' : '📄 Contract'} {group.scope_id}
              </span>
              <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                {group.checklist_type_name || 'Unknown'} · type_id={group.checklist_type_id ?? '—'}
              </span>
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                office={group.office_guid || '—'}
              </span>
              <span style={{
                marginLeft: 'auto', fontSize: '0.85rem',
                color: pct === 100 ? '#10b981' : '#6b7280',
              }}>
                {uploadedCount}/{group.items.length} uploaded ({pct}%)
              </span>
              <span style={{ fontSize: '0.8rem', color: group.firestore_mapping_loaded ? '#10b981' : '#f59e0b' }}>
                {group.firestore_mapping_loaded ? '✅ Firestore mapping' : '⚠️ no mapping yet'}
              </span>

              {/* Refresh mapping button — stop propagation so it doesn't toggle expand */}
              {canEdit && (
                <button
                  onClick={e => { e.stopPropagation(); handleRefreshMapping(group, groupIndex); }}
                  disabled={gStatus === 'refreshing' || gStatus === 'saving'}
                  style={{
                    fontSize: '0.75rem', padding: '2px 8px', borderRadius: '4px',
                    border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer',
                    color: '#374151',
                  }}
                  title="Rebuild Firestore mapping (phrases → LLM → keyword); preserves drag-and-drop edits"
                >
                  {gStatus === 'refreshing' ? '⏳ rebuilding…' : '↻ Rebuild mapping'}
                </button>
              )}

              {/* Save/error feedback */}
              {gStatus === 'saving' && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>⏳ saving…</span>}
              {gStatus === 'done'   && <span style={{ fontSize: '0.8rem', color: '#10b981' }}>✅ saved</span>}
              {gStatus.startsWith('error:') && (
                <span style={{ fontSize: '0.8rem', color: '#ef4444' }} title={gStatus.slice(6)}>❌ error</span>
              )}

              <span style={{ color: '#9ca3af' }}>{isExpanded ? '▲' : '▼'}</span>
            </div>

            {/* Items table */}
            {isExpanded && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={thStyle}>Item Name</th>
                    <th style={thStyle}>Item ID</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>
                      Mapped Doc Type(s)
                      {canEdit && isDraggingInGroup && (
                        <span style={{ marginLeft: '8px', color: '#3b82f6', fontWeight: 400, fontSize: '0.78rem' }}>
                          ← drop on a row to remap
                        </span>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => {
                    const isDropTarget = dropTarget?.groupIndex === groupIndex && dropTarget?.itemId === item.id;
                    return (
                      <tr
                        key={item.id}
                        onDragOver={e => handleDragOver(e, groupIndex, item.id, group.checklist_type_id)}
                        onDragLeave={handleDragLeave}
                        onDrop={e => handleDrop(e, group, item.id, item.item_name.toLowerCase())}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          background: isDropTarget ? '#eff6ff' : undefined,
                          outline: isDropTarget ? '2px solid #3b82f6' : undefined,
                          outlineOffset: '-2px',
                          transition: 'background 0.1s',
                        }}
                      >
                        <td style={tdStyle}>{item.item_name}</td>
                        <td style={{ ...tdStyle, color: '#9ca3af', fontFamily: 'monospace' }}>
                          {item.tms_checklist_item_id}
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem',
                            background: (STATUS_COLORS[item.status] || '#6b7280') + '22',
                            color: STATUS_COLORS[item.status] || '#6b7280',
                            fontWeight: 500,
                          }}>
                            {item.status}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {item.mapped_doc_types.length > 0 ? (
                            item.mapped_doc_types.map(dt => (
                              <span
                                key={dt}
                                draggable={canEdit}
                                onDragStart={e => canEdit && handleDragStart(e, dt, groupIndex)}
                                onDragEnd={handleDragEnd}
                                title={canEdit ? 'Drag to remap to a different checklist item' : undefined}
                                style={{
                                  display: 'inline-block',
                                  marginRight: '4px',
                                  marginBottom: '2px',
                                  padding: '1px 6px',
                                  borderRadius: '3px',
                                  fontSize: '0.78rem',
                                  background: '#dbeafe',
                                  color: '#1d4ed8',
                                  cursor: canEdit ? 'grab' : 'default',
                                  opacity: dragState?.doc_type === dt && dragState?.groupIndex === groupIndex ? 0.4 : 1,
                                  userSelect: 'none',
                                }}
                              >
                                {dt}
                              </span>
                            ))
                          ) : (
                            <span style={{ color: '#d1d5db', fontSize: '0.8rem' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {isExpanded && (canEdit || (group.unmapped_doc_types || []).length > 0) && (() => {
              const unmappedTypes = group.unmapped_doc_types || [];
              const isUnmappedDropTarget = dropTarget?.groupIndex === groupIndex
                && dropTarget?.itemId === UNMAPPED_DROP_ID;
              return (
              <div
                onDragOver={e => canEdit && handleDragOver(e, groupIndex, UNMAPPED_DROP_ID, group.checklist_type_id)}
                onDragLeave={handleDragLeave}
                onDrop={e => canEdit && handleDropToUnmapped(e, group, groupIndex)}
                style={{
                  padding: '10px 16px',
                  borderTop: '1px solid #e5e7eb',
                  background: isUnmappedDropTarget ? '#eff6ff' : '#fafafa',
                  outline: isUnmappedDropTarget ? '2px solid #3b82f6' : undefined,
                  outlineOffset: '-2px',
                  minHeight: canEdit ? '48px' : undefined,
                  transition: 'background 0.1s',
                }}
              >
                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '6px' }}>
                  Unmapped doc types ({unmappedTypes.length})
                  {canEdit && isDraggingInGroup && (
                    <span style={{ marginLeft: '8px', color: '#3b82f6' }}>
                      ← drop here to unmap
                    </span>
                  )}
                  {canEdit && !isDraggingInGroup && unmappedTypes.length > 0 && (
                    <span style={{ marginLeft: '8px' }}>
                      — drag onto a row to assign, or drag mapped types here to unmap
                    </span>
                  )}
                  {canEdit && !isDraggingInGroup && unmappedTypes.length === 0 && (
                    <span style={{ marginLeft: '8px' }}>
                      — drag mapped types here to unmap
                    </span>
                  )}
                </div>
                <div>
                  {unmappedTypes.length > 0 ? unmappedTypes.map(dt => (
                    <span
                      key={dt}
                      draggable={canEdit}
                      onDragStart={e => canEdit && handleDragStart(e, dt, groupIndex)}
                      onDragEnd={handleDragEnd}
                      title={canEdit ? 'Drag onto a checklist item row to map this doc type' : undefined}
                      style={{
                        display: 'inline-block',
                        marginRight: '4px',
                        marginBottom: '2px',
                        padding: '1px 6px',
                        borderRadius: '3px',
                        fontSize: '0.78rem',
                        background: '#f3f4f6',
                        color: '#6b7280',
                        cursor: canEdit ? 'grab' : 'default',
                        opacity: dragState?.doc_type === dt && dragState?.groupIndex === groupIndex ? 0.4 : 1,
                        userSelect: 'none',
                        border: isDraggingInGroup ? '1px dashed #9ca3af' : '1px solid transparent',
                      }}
                    >
                      {dt}
                    </span>
                  )) : (
                    canEdit && (
                      <span style={{ color: '#d1d5db', fontSize: '0.8rem' }}>
                        No unmapped doc types
                      </span>
                    )
                  )}
                </div>
              </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}

const thStyle = {
  textAlign: 'left',
  padding: '8px 12px',
  borderBottom: '1px solid #e5e7eb',
  color: '#6b7280',
  fontWeight: 500,
};

const tdStyle = {
  padding: '8px 12px',
  verticalAlign: 'top',
};

export default DeveloperSkySlopeChecklists;
